import React, { useMemo } from "react";

/** Recursive, safe JSON viewer. No `dangerouslySetInnerHTML` — we
 * render React nodes directly, so user-supplied JSON content can
 * never inject HTML. */
function tokenise(value, depth = 0, path = "root") {
    const pad = "  ".repeat(depth);
    if (value === null) return <span className="json-null">null</span>;
    if (typeof value === "boolean") {
        return <span className="json-bool">{String(value)}</span>;
    }
    if (typeof value === "number") {
        return <span className="json-number">{String(value)}</span>;
    }
    if (typeof value === "string") {
        return <span className="json-string">{JSON.stringify(value)}</span>;
    }
    if (Array.isArray(value)) {
        if (value.length === 0) return <span>[]</span>;
        return (
            <>
                {"["}
                {value.map((v, i) => (
                    <React.Fragment key={`${path}.${i}`}>
                        {"\n"}{pad}{"  "}
                        {tokenise(v, depth + 1, `${path}.${i}`)}
                        {i < value.length - 1 ? "," : ""}
                    </React.Fragment>
                ))}
                {"\n"}{pad}{"]"}
            </>
        );
    }
    if (value && typeof value === "object") {
        const entries = Object.entries(value);
        if (entries.length === 0) return <span>{"{}"}</span>;
        return (
            <>
                {"{"}
                {entries.map(([k, v], i) => (
                    <React.Fragment key={`${path}.${k}`}>
                        {"\n"}{pad}{"  "}
                        <span className="json-key">{JSON.stringify(k)}</span>
                        {": "}
                        {tokenise(v, depth + 1, `${path}.${k}`)}
                        {i < entries.length - 1 ? "," : ""}
                    </React.Fragment>
                ))}
                {"\n"}{pad}{"}"}
            </>
        );
    }
    // Anything else (undefined, symbols, functions) — render as a string
    return <span>{String(value)}</span>;
}

export default function JsonViewer({ data, maxHeight = 520, testId = "json-viewer" }) {
    const tree = useMemo(() => {
        // Accept a JSON string or any value. If a string is passed, try
        // to parse — fall back to rendering it as-is.
        let v = data;
        if (typeof data === "string") {
            try { v = JSON.parse(data); } catch (_e) { v = data; }
        }
        return tokenise(v);
    }, [data]);
    return (
        <pre
            data-testid={testId}
            className="json-viewer"
            style={{ maxHeight }}
        >{tree}</pre>
    );
}
